import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon } from 'lucide-react';
import { TrophyGlyph } from '../components/icons/BeelyvIcons';
import { Avatar } from '../components/Avatar';
import { MetricCard } from '../components/MetricCard';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/Button';
import { Card, SectionTitle } from '../components/ui/Card';
import { biomarkerDefs } from '../data/biomarkers';
import { people } from '../data/people';
import { metricsForSport, sports } from '../data/sportMetrics';
import { currentUser } from '../data/user';
import { useBiomarkers } from '../contexts/BiomarkersContext';
import { useLocale } from '../contexts/LocaleContext';
import { useMetrics } from '../contexts/MetricsContext';
import { useProgress } from '../contexts/ProgressContext';
import { useSettings } from '../contexts/SettingsContext';
import { average, buildHistory } from '../utils/history';
import { convertLabValue, formatLabValue, formatMetric } from '../utils/units';

/**
 * MOB/Profile/PublicPreview — the logged user's profile exactly as others see
 * it. Every section is driven by an explicit visibility toggle.
 */
export function OwnPublicProfile() {
  const {
    t,
    tl,
    locale
  } = useLocale();
  const navigate = useNavigate();
  const metrics = useMetrics();
  const progress = useProgress();
  const bio = useBiomarkers();
  const {
    privacy
  } = useSettings();
  const me = people[0];
  const showBadges = privacy.publicAchievements && privacy.publicBadges;
  const showStreak = privacy.publicStreak;
  const history = useMemo(() => buildHistory(30, currentUser.streak), []);
  const sportPublic = privacy.publicSportMetrics ? sports.flatMap(sport => metricsForSport(sport).filter(metric => metric.value > 0 && metrics.isPublic(metric.id))) : [];
  const bodyPublic = privacy.publicBodyMetrics ? metrics.bodyMetrics.filter(metric => !metrics.isHidden(metric.id) && metrics.isPublic(metric.id)) : [];
  const bioPublic = privacy.publicBiomarkers ? biomarkerDefs.filter(def => bio.isPublic(def.id)).map(def => ({
    def,
    latest: bio.recordsFor(def.id)[0]
  })).filter(item => item.latest) : [];
  const nothingPublic = !showBadges && !showStreak && sportPublic.length === 0 && !privacy.publicProgress && bodyPublic.length === 0 && bioPublic.length === 0;
  return <div className="px-2 pb-2" data-id="element-3343" data-mp="1azv67xe1ysrz">
      <PageHeader title={t('preview.title')} showBack data-id="element-3344" data-mp="162v1bpsxvw2g" />

      <p className="mb-2 flex items-start gap-2 rounded-card border border-gold/35 bg-white/60 p-2 text-[12px] leading-snug text-deep" data-id="element-3345" data-mp="161bl1z7a9l0o">
        <EyeIcon size={14} className="mt-[1px] shrink-0 text-gold-deep" aria-hidden="true" data-id="element-3346" data-mp="x2zpdirk7x1d" />
        {t('preview.banner')}
      </p>

      <Card className="p-2" data-id="element-3347" data-mp="3lvu1njxhkubu">
        <div className="flex items-start gap-2" data-id="element-3348" data-mp="3lrxu2pk6mwkc">
          <Avatar src={me.avatar} name={metrics.profile.name} size={68} data-id="element-3349" data-mp="3hrm8najzws7t" />
          <div className="min-w-0 flex-1" data-id="element-3350" data-mp="1xb6dx68zdcey">
            {showStreak && <p className="bee-overline text-[9px] font-bold text-gold-deep" data-id="element-3351" data-mp="1x8tn03l8vrpr">
                {t('preview.streak', {
              n: progress.streak
            })}
              </p>}
            <p className="mt-[2px] truncate text-[17px] font-bold text-deep" data-id="element-3352" data-mp="269rpuijy5t6r">
              {metrics.profile.name}
            </p>
            <p className="truncate text-[12px] text-muted" data-id="element-3353" data-mp="2g14l7k4yf1hj">{metrics.profile.username}</p>
          </div>
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-deep" data-id="element-3354" data-mp="2kzz4soaqwu2p">{metrics.profile.bio}</p>
      </Card>

      {nothingPublic && <p className="mt-3 px-1 text-[12px] leading-snug text-muted" data-id="element-3355" data-mp="2p343sgvmadml">{t('preview.hiddenAll')}</p>}

      {showBadges && <section className="mt-3" data-id="element-3356" data-mp="2tyt4wonac2l0">
          <SectionTitle data-id="element-3357" data-mp="uob8kiynbov6">{t('profile.achievements')}</SectionTitle>
          <ul className="flex flex-wrap gap-1" data-id="element-3358" data-mp="upw68hg9bnx9">
            {me.badges.map(badge => <li key={badge} className="flex min-h-[38px] items-center gap-1 rounded-pill border border-gold/45 bg-white/70 px-2 text-[12px] font-medium text-deep" data-id="element-3359" data-mp="zn4kw82759tr">
                <TrophyGlyph size={16} className="text-gold-deep" aria-hidden="true" data-id="element-3360" data-mp="3vld9ommy5nll" />
                {t(badge)}
              </li>)}
          </ul>
        </section>}

      {sportPublic.length > 0 && <section className="mt-3" data-id="element-3361" data-mp="3hrcj4g65f6wb">
          <SectionTitle data-id="element-3362" data-mp="3hsdygwu76fju">{t('health.sports')}</SectionTitle>
          <div className="grid grid-cols-2 gap-2" data-id="element-3363" data-mp="3csrl8nedczb1">
            {sportPublic.map(metric => <MetricCard key={metric.id} label={tl(metric.label)} value={formatMetric(metric.kind, metric.value, metrics.unitSystem)} data-id="element-3364" data-mp="3rh5kkocku5mu" />)}
          </div>
        </section>}

      {privacy.publicProgress && <section className="mt-3" data-id="element-3365" data-mp="2u21h6vd2b6gl">
          <SectionTitle data-id="element-3366" data-mp="2u0yqanfqp40o">{t('preview.progress')}</SectionTitle>
          <MetricCard label={t('home.summary.lifescore')} value={t('preview.progressValue', {
        n: average(history)
      })} data-id="element-3367" data-mp="19jzawunowlq3" />
        </section>}

      {bodyPublic.length > 0 && <section className="mt-3" data-id="element-3368" data-mp="zm2ylzj0nkqe">
          <SectionTitle data-id="element-3369" data-mp="zn4bm9jlehar">{t('preview.body')}</SectionTitle>
          <div className="grid grid-cols-2 gap-2" data-id="element-3370" data-mp="14kmogb0qdgo1">
            {bodyPublic.map(metric => <MetricCard key={metric.id} label={t(`metrics.${metric.id}`)} value={formatMetric(metric.kind, metric.value, metrics.unitSystem)} data-id="element-3371" data-mp="1saqh5ncdrw09" />)}
          </div>
        </section>}

      {bioPublic.length > 0 && <section className="mt-3" data-id="element-3372" data-mp="1nb435boqabqe">
          <SectionTitle data-id="element-3373" data-mp="1nackt2k56ai2">{t('preview.biomarkers')}</SectionTitle>
          <div className="grid grid-cols-2 gap-2" data-id="element-3374" data-mp="7uvk7k2n0f51">
            {bioPublic.map(({
          def,
          latest
        }) => {
          const unit = bio.unitFor(def.id);
          const shown = convertLabValue(latest!.value, def.canonicalUnit, unit, def.factors);
          return <MetricCard key={def.id} label={tl(def.name)} value={`${formatLabValue(shown, def.decimals[unit] ?? 0, locale)} ${unit}`} data-id="element-3375" data-mp="29kb5rza0tqbi" />;
        })}
          </div>
        </section>}

      <Button variant="outline" size="lg" fullWidth className="mt-3" onClick={() => navigate('/perfil/editar')} data-id="element-3376" data-mp="1vtfru8ko0j12">
        {t('preview.edit')}
      </Button>
    </div>;
}